import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { type Option, SearchSelect } from '~/styles/forms/SearchSelect';
import { formatString } from '~/utils/stringFormat';
import {
  type BoardTarget,
  InfoHeading,
  InfoTable,
  ModalParagraph,
  StudentBoardModal,
  StudentInfoBlock,
  TargetSelect,
  targetLabel,
  useAdoptionErrorContent,
} from './StudentBoardModal';
import {
  type AssignCandidate,
  PEOPLE_PICKER_DEBOUNCE_MS,
  useAssignCandidates,
} from './useAssignCandidates';
import { useStudentAdoptionInfo } from './useStudentAdoptionInfo';

interface AssignStudentModalProps {
  /** The student to assign. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  onClose: () => void;
  /** Fired once the assignment has landed, so the board can refresh behind it. */
  onAssigned?: () => void;
}

interface AssignedSummary {
  studentName: string;
  studyHelperName: string;
}

/**
 * Legacy's two-step assign popup — `AssignStudentInteractivePopup.php:15-56`,
 * popups 21904 and 21905.
 *
 * The body is TWO info tables, not one: `adoption_info.php` (the same Student
 * Information block the adopt modal renders) followed by `assign_info.php` (the
 * coordinator's own name, and the study-helper picker).
 *
 * The picker is BOUND to the web target above it. `load_study_helpers` derives
 * the group from the posted `campaign_id`/`web_ad_id` and returns `[]` when
 * neither is posted (`:64-75`), and both target controls carry
 * `data-reset-on-change="student-board:assign-study-helper"`
 * (`adoption_info.php:40,51`) — so the target select comes first, the roster is
 * keyed on it, and changing it clears whoever was chosen. Without that reset the
 * confirm can post group Y's web ad with group X's leader, which the server then
 * 403s on a name the picker itself served (spec §5.3).
 */
export function AssignStudentModal({
  studentId,
  onClose,
  onAssigned,
}: AssignStudentModalProps) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const [assigned, setAssigned] = useState<AssignedSummary | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  // Held as id AND name: the success sentence names the helper, and by the time
  // it renders the roster query may have been re-run under a different search
  // term and no longer carry the row that was picked.
  const [candidate, setCandidate] = useState<AssignCandidate | null>(null);
  // What is being typed, and what the roster query is keyed on. Legacy's select2
  // debounces at `delay: 250` (`main-script.js:2745`); without it every
  // keystroke is its own request.
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  /** Which target the current candidate was chosen against — see the reset below. */
  const [candidateFor, setCandidateFor] = useState<string | null>(null);
  // Which student the modal is showing RIGHT NOW, readable from a settled
  // request — see AdoptStudentModal for why a settled POST must not write its
  // success body into whatever modal is open by then.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight; `isPending` disables the button a render late,
  // and a second confirm would adopt a student who is already off the board.
  const submitting = useRef(false);

  // Reopening for a different student must not inherit the last one's success
  // body, target choice or candidate. Adjusting state during render (rather than
  // in an effect) keeps the first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setAssigned(null);
    setSelectedTargetId(null);
    setCandidate(null);
    setTerm('');
    setSearch('');
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(term), PEOPLE_PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // `mode=assign` runs no adoption ladder — the target helper is not chosen yet,
  // and legacy's `verify()` validates nothing (`:16-32`) — but it does apply the
  // coordinator gate and narrows the web-target list to groups this caller
  // ADMINs, where adopt's list is mod-or-admin.
  const { info, isLoading, error } = useStudentAdoptionInfo(
    studentId,
    assigned === null,
    'assign',
  );
  const { mutateAsync: assignStudent, isPending } =
    api.assignStudent.useMutation();

  // The picker's population is ORIGINAL, not available — legacy keeps the
  // passed-on targets and marks them (`adoption_info.php:50-60`) — and the
  // campaign, when there is one, suppresses the web-ad select entirely
  // (`:33-58`, the rule `StudentInfoBlock` already enforces).
  const targets = info?.original_targets ?? [];
  const postedTargetId =
    targets.find((target) => target.target_id === selectedTargetId)
      ?.target_id ??
    targets[0]?.target_id ??
    null;
  const campaignId = info?.matched_campaign?.campaign_id;
  const webAdId =
    campaignId === undefined && postedTargetId !== null
      ? postedTargetId
      : undefined;

  // Legacy's `data-reset-on-change`, as the same render-time reset `openedFor`
  // above uses: an effect would clear the choice one paint AFTER the roster had
  // already been re-keyed, leaving a candidate from the previous group selected
  // and postable for that one frame. The roster itself refetches on its own,
  // because both ids are part of the query key.
  const targetKey =
    campaignId === undefined
      ? `web-ad:${postedTargetId}`
      : `campaign:${campaignId}`;
  if (candidateFor !== targetKey) {
    setCandidateFor(targetKey);
    setCandidate(null);
    setTerm('');
    setSearch('');
  }

  const {
    candidates,
    count: candidateCount,
    isFetching: candidatesFetching,
    error: candidatesError,
  } = useAssignCandidates(
    assigned === null ? studentId : null,
    campaignId,
    webAdId,
    search,
  );

  const candidateOptions = useMemo<Option[]>(
    () =>
      candidates.map((candidate) => ({
        value: String(candidate.id),
        // The id rides in the label for the reason W2.4 D12 records on the
        // reassign pickers: two leaders of one congregation can display the same
        // name, and then the name alone does not identify the option.
        label: `${candidate.display_name} (#${candidate.id})`,
      })),
    [candidates],
  );

  // The roster's own refusal counts as a failure of the modal, not of one
  // control: the only way to reach it is a posted target outside
  // `assignable_group_ids`, and the one shape that produces that is a student
  // carrying BOTH a study-group campaign (which `adoption-info` does not filter)
  // and an admin-visible web ad (which the campaign then suppresses) — 2
  // students today, spec §5.3. Surfacing it beats a picker that silently stays
  // empty.
  const failure = submitError ?? error ?? candidatesError;
  const errorContent = useAdoptionErrorContent(
    failure,
    info?.display_name ?? '',
  );

  if (studentId === null) return null;

  const handleClose = (): void => {
    setAssigned(null);
    setSubmitError(null);
    onClose();
  };

  // Success first: the adoption has landed, so nothing that follows can undo it.
  if (assigned !== null) {
    return (
      <StudentBoardModal
        title={t('wbs.assignment-successful')}
        onClose={handleClose}
        footer={
          <WBSButton alternate onClick={handleClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {formatString(
            t('messages.s-successfully-assigned-to'),
            assigned.studentName,
            assigned.studyHelperName,
          )}
        </ModalParagraph>
      </StudentBoardModal>
    );
  }

  // A typed 4xx from `/adoption-info` arrives INSTEAD of the payload, so the
  // confirm body never renders and the modal opens straight into the error —
  // legacy's own shape. `POST /assign`'s refusals and the roster's own 404 land
  // in the same place, so there is one error path and not three.
  if (failure) {
    return (
      <StudentBoardModal
        title={errorContent.title ?? t('wbs.assign-to-your-study-helper')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>{errorContent.body}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (isLoading || !info) {
    return (
      <StudentBoardModal
        title={t('wbs.assign-to-your-study-helper')}
        onClose={onClose}
      >
        <ModalParagraph>{t('wbs.loading')}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  // "No candidates at all", not "no match for what is typed": measured
  // 2026-08-18, 19 of the 56 rows that can render this button open on an empty
  // picker, because the caller is excluded from their own roster and 19 of those
  // groups have exactly one ADMIN. An unsearched empty page is that state.
  const rosterEmpty =
    search === '' && !candidatesFetching && candidateCount === 0;

  const handleAssign = async (): Promise<void> => {
    if (submitting.current || candidate === null) return;
    submitting.current = true;
    const submittedFor = studentId;
    setSubmitError(null);
    try {
      await assignStudent({
        body: {
          user_id: studentId,
          study_helper_id: candidate.id,
          // Exactly one attribution, or neither — the same rule the adopt modal
          // posts under. A matched campaign suppresses the target picker, and
          // posting both is `AdoptionTargetConflictError`.
          campaign_id: campaignId,
          web_ad_id: webAdId,
        },
      });
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    // The write landed either way, so the board is stale either way; only the
    // modal body is conditional on this still being the student on screen.
    onAssigned?.();
    if (openSession.current === submittedFor) {
      setAssigned({
        studentName: info.display_name,
        studyHelperName: candidate.display_name,
      });
    }
  };

  return (
    <StudentBoardModal
      title={t('wbs.assign-to-your-study-helper')}
      onClose={onClose}
      overflowVisible
      footer={
        // Suppressed rather than disabled when there is nobody to assign to:
        // legacy renders an empty select2 and lets the confirm fail server-side,
        // and on 19 of 56 rows that is the whole interaction. With candidates,
        // the button waits for a choice — legacy would post `study-helper-id=0`
        // and answer an untranslated slug.
        rosterEmpty ? undefined : (
          <WBSButton
            onClick={() => {
              void handleAssign();
            }}
            isLoading={isPending}
            disabled={candidate === null}
          >
            {/* Legacy's confirm label is the ACF `confirm_button_text` on
                popup 21904, measured 2026-08-18 as `Assign` / `Asignar` /
                `Attribuer` / `Conceder` (catalogue unit 124834, four
                languages). No `wbs.assign` key exists yet — adding one is a
                four-file edit to a serialization point — so this uses the
                nearest already-translated key. What it must NOT be is the row
                action's own `wbs.assign-to` ("Assign to..."): two buttons on
                screen at once under one accessible name. */}
            {t('wbs.assign-to-2')}
          </WBSButton>
        )
      }
    >
      <ModalParagraph>{t('wbs.as-a-group-coordinator-you-can')}</ModalParagraph>
      <StudentInfoBlock
        info={info}
        targets={targets}
        renderTargets={(options: readonly BoardTarget[]) => (
          <TargetSelect
            aria-label={t('wbs.matched-web-ad-targets')}
            value={String(postedTargetId ?? '')}
            onChange={(event) =>
              setSelectedTargetId(Number(event.target.value))
            }
          >
            {options.map((target) => (
              <option key={target.target_id} value={String(target.target_id)}>
                {target.has_passed_on
                  ? `${targetLabel(target)} ${t('wbs.passed-on-suffix')}`
                  : targetLabel(target)}
              </option>
            ))}
          </TargetSelect>
        )}
      />
      <InfoHeading>{t('wbs.assign-information')}</InfoHeading>
      <InfoTable>
        <tbody>
          <tr>
            <th>{t('wbs.assign-name')}</th>
            {/* `bp_core_get_user_displayname($coordinator_id)` — the caller, who
                is never posted: the server reads the assignee off the session
                (`AssignStudentInteractivePopup.php:26-28`). */}
            <td>{me?.display_name ?? ''}</td>
          </tr>
          <tr>
            {/* `_ex('Study Helper:', 'assign popup assign info lable', 'wbs')`
                — the colon-bearing key, matching the row above it. */}
            <th>{t('lessons.study-helper-2')}</th>
            <td>
              {rosterEmpty ? (
                <EmptyPickerNotice>
                  {t('wbs.no-study-helpers-to-assign')}
                </EmptyPickerNotice>
              ) : (
                <SearchSelect
                  multi={false}
                  value={candidate === null ? [] : [String(candidate.id)]}
                  options={candidateOptions}
                  onChange={(next) =>
                    setCandidate(
                      candidates.find((row) => String(row.id) === next[0]) ??
                        null,
                    )
                  }
                  // Server-side search, as legacy's select2 is — but with no
                  // minimum: legacy needs three characters because its ajax is
                  // the only way to see any name at all, while this control
                  // opens on the group's first page of leaders, which for every
                  // measured group is the whole roster.
                  onSearch={setTerm}
                  loading={term !== search || candidatesFetching}
                  placeholder={t('wbs.select-a-value')}
                />
              )}
            </td>
          </tr>
        </tbody>
      </InfoTable>
    </StudentBoardModal>
  );
}

/**
 * Legacy has no such message — it renders an empty select2 and says nothing —
 * so this is new copy, keyed `wbs.no-study-helpers-to-assign` and English-only.
 */
const EmptyPickerNotice = styled.span`
  color: var(--grey-700);
  font-family: var(--raleway);
  font-size: 14px;
`;
